// @ts-check

import React, { useMemo, useState } from "react";

import { BlockStack, Button, Card, Grid, InlineStack, Text, Thumbnail } from "@shopify/polaris"
import { ChatIcon } from "@shopify/polaris-icons";

import { OrdersBySource } from "components/cards/OrdersBySource/OrdersBySource";
import { RevenueBySource } from "components/cards/RevenueBySource/RevenueBySource";
import { LockableCard } from "components/cards/LockableCard/LockableCard";
import { OrdersLineChartCard } from "components/cards/OrdersLineChartCard/OrdersLineChartCard";
import { RevenueLineChartCard } from "components/cards/RevenueLineChartCard/RevenueLineChartCard";
import { AOVLineChartCard } from "components/cards/AOVLineChartCard/AOVLineChartCard";
import { TopMetricsCard } from "components/cards/TopMetricsCard/TopMetricsCard";
import { RequestReportModal } from "components/modals/RequestReportModal";
import { UtmSetsPreviewTable } from "components/UtmSetsTable/UtmSetsPreviewTable";
import { DashboardLockedCard } from "components/dashboard/DashboardLockedCard/DashboardLockedCard";
import { FetchOrdersControls } from "components/FetchOrderControls/FetchOrdersControls";

import {
  SAMPLE_LINE_SALES_DATA,
  SAMPLE_LINE_REVENUE_DATA,
  SAMPLE_SALES_SOURCE_1_DATA,
  SAMPLE_SALES_SOURCE_2_DATA,
  SAMPLE_REVENUE_BY_SOURCE_1_DATA,
  SAMPLE_REVENUE_BY_SOURCE_2_DATA,
} from "components/dashboard/util/sample-data";

import { useGlobalContext } from "context/GlobalContext";

import timGreenHeadshot from "assets/tim-green.jpg";

import "./Dashboard.css";

const DashboardContent = () => {
  const {
    locked,
    isPlaceholderMetrics,
    isRefetchingMetrics,
    utmSets,
    isFetchingUtmSets,
    isLoadingMetrics,
    totalRevenue,
    revenueByDay,
    totalSales,
    salesByDay,
    aovByDay,
    totalAov,
    salesBySource,
    revenueBySource,
  } = useGlobalContext();

  const [requestReportModalOpen, setRequestReportModalOpen] = useState(false);

  const handleRequestReportClick = () => {
    setRequestReportModalOpen(true);
  };

  const ordersBySourceData = useMemo(() => {
    if (locked) {
      return [
        { name: "Source 1", data: SAMPLE_SALES_SOURCE_1_DATA },
        { name: "Source 2", data: SAMPLE_SALES_SOURCE_2_DATA },
      ]
    }

    return salesBySource?.data;
  }, [locked, salesBySource]);

  const revenueBySourceData = useMemo(() => {
    if (locked) {
      return [
        { name: "Source 1", data: SAMPLE_REVENUE_BY_SOURCE_1_DATA },
        { name: "Source 2", data: SAMPLE_REVENUE_BY_SOURCE_2_DATA },
      ]
    }

    return revenueBySource?.data;
  }, [locked, revenueBySource]);

  return (
    <BlockStack gap="200">
      <RequestReportModal
        open={requestReportModalOpen}
        setOpen={setRequestReportModalOpen}
      />

      <FetchOrdersControls loading={isPlaceholderMetrics && isRefetchingMetrics} />

      <Grid>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <OrdersLineChartCard
              title="Orders from UTM links"
              dataTitle="UTM orders"
              loading={isLoadingMetrics}
              data={locked ? SAMPLE_LINE_SALES_DATA : salesByDay?.data}
              totalOrders={locked ? SAMPLE_LINE_SALES_DATA.reduce((acc, curr) => acc + curr.value, 0) : totalSales?.data}
            />
          </LockableCard>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <RevenueLineChartCard
              title="Revenue from UTM links"
              dataTitle="UTM revenue"
              loading={isLoadingMetrics}
              data={locked ? SAMPLE_LINE_REVENUE_DATA : revenueByDay?.data}
              totalRevenue={locked ? SAMPLE_LINE_REVENUE_DATA.reduce((acc, curr) => acc + curr.value, 0) : totalRevenue?.data}
            />
          </LockableCard>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <AOVLineChartCard
              title="AOV from UTM links"
              dataTitle="UTM AOV"
              loading={isLoadingMetrics}
              data={locked ? SAMPLE_LINE_SALES_DATA : aovByDay?.data}
              totalAov={locked ? SAMPLE_LINE_SALES_DATA.reduce((acc, curr) => acc + curr.value, 0) : totalAov?.data}
            />
          </LockableCard>
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
          <LockableCard
            loading={isPlaceholderMetrics && isRefetchingMetrics}
            loadingCardHeight="300px"
          >
            <OrdersBySource
              title="Orders by source"
              loading={isLoadingMetrics}
              data={ordersBySourceData}
              error={locked ? null : salesBySource?.error}
            />
          </LockableCard>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
          <LockableCard
            loading={isPlaceholderMetrics && isRefetchingMetrics}
            loadingCardHeight="300px"
          >
            <RevenueBySource
              title="Revenue by source"
              loading={isLoadingMetrics}
              data={revenueBySourceData}
              error={locked ? null : revenueBySource?.error}
            />
          </LockableCard>
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 12, xl: 12 }}>
          <LockableCard locked={locked} loading={isFetchingUtmSets}>
            <UtmSetsPreviewTable utmSets={utmSets} />
          </LockableCard>
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard locked={locked} loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <TopMetricsCard
              metricTitle="UTM Source"
              metricKey="utm_source"
              calculateViewAllHref={() => `/reports/top-utm-sources`}
              calculateReportHref={(value) => `/reports/source/${encodeURIComponent(value)}`}
              calculateOrdersHref={(value) => `/orders?utm_source=${encodeURIComponent(value)}`}
            />
          </LockableCard>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard locked={locked} loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <TopMetricsCard
              metricTitle="UTM Medium"
              metricKey="utm_medium"
              calculateViewAllHref={() => `/reports/top-utm-mediums`}
              calculateReportHref={(value) => `/reports/medium/${encodeURIComponent(value)}`}
              calculateOrdersHref={(value) => `/orders?utm_medium=${encodeURIComponent(value)}`}
            />
          </LockableCard>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard locked={locked} loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <TopMetricsCard
              metricTitle="UTM Campaign"
              metricKey="utm_campaign"
              calculateViewAllHref={() => `/reports/top-utm-campaigns`}
              calculateReportHref={(value) => `/reports/campaign/${encodeURIComponent(value)}`}
              calculateOrdersHref={(value) => `/orders?utm_campaign=${encodeURIComponent(value)}`}
            />
          </LockableCard>
        </Grid.Cell>

        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard locked={locked} loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <TopMetricsCard
              metricTitle="UTM Content"
              metricKey="utm_content"
              calculateViewAllHref={() => `/reports/top-utm-contents`}
              calculateOrdersHref={(value) => `/orders?utm_content=${encodeURIComponent(value)}`}
            />
          </LockableCard>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <LockableCard locked={locked} loading={isPlaceholderMetrics && isRefetchingMetrics}>
            <TopMetricsCard
              metricTitle="UTM Term"
              metricKey="utm_term"
              calculateViewAllHref={() => `/reports/top-utm-terms`}
              calculateOrdersHref={(value) => `/orders?utm_term=${encodeURIComponent(value)}`}
            />
          </LockableCard>
        </Grid.Cell>
        <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
          <Card>
            <BlockStack gap="600" inlineAlign="start">
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">
                  How can we improve Origin?
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  We're always looking to improve and would love your feedback.
                </Text>
              </BlockStack>

              {/* <InlineStack gap="200">
                <Thumbnail
                  source={timGreenHeadshot}
                  alt="Tim Green"
                  size="small"
                />

                <BlockStack align="center">
                  <Text variant="bodyMd" fontWeight="bold" as="p">Tim Green</Text>
                  <Text variant="bodyMd" as="p" tone="subdued">Founder, Origin UTM Tracking</Text>
                </BlockStack>
              </InlineStack> */}

              <Button onClick={handleRequestReportClick} icon={ChatIcon}>
                Share your feedback
              </Button>
            </BlockStack>
          </Card>
        </Grid.Cell>
      </Grid>
    </BlockStack>
  );
};

export const Dashboard = () => {
  const { locked } = useGlobalContext();

  let content = <DashboardContent />;

  if (locked) {
    content = (
      <div className="locked-dashboard">
        <div
          className="locked-dashboard-content"
          inert=""
          aria-hidden="true"
          tabIndex={-1}
        >
          {content}
        </div>

        <DashboardLockedCard />
      </div>
    );
  }

  return content;
}
